import { useTheme } from '../../../contexts/theme-context';
import {
  ContentSection,
  ContinueButton,
} from '../../registration/pages/secure-your-account/secure-your-account.styled';
import {
  Breaker,
  CardAndIconContainer,
  Heading,
  Icon,
  InfoText,
  RedirectLink,
  SubHeading,
  TextContainer,
  WhatYouNeedToKnow,
  WhiteCard,
} from './all-set.styled';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveProfile } from '../../../helpers/save-profile';
import type { OnboardingStepProps } from '../../registration/registration';
import { useAuth } from '../../../contexts/auth/auth-context';

export const AllSetStep: React.FC<OnboardingStepProps> = () => {
  const { isLight } = useTheme();
  const { onboarding, setRegistration, syncSession } = useAuth();
  const navigate = useNavigate();

  const [isFinishing, setIsFinishing] = useState(false);











  const [error, setError] = useState<string | null>(null);

  const handleFinish = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();

    if (isFinishing) return;
    setIsFinishing(true);
    setError(null);

    try {
      const patch = { registration_completed: true };

      await saveProfile({ ...onboarding, ...patch });
      setRegistration(patch);

      await syncSession();

      navigate('/onboarding', { replace: true });
    } catch (err) {
      console.error('Failed to save completion status:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsFinishing(false);
    }
  };

  return (
    <ContentSection style={{ paddingTop: 16, paddingBottom: 24, overflowY: 'auto' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
        }}
      >
        <Heading isLight={isLight}>
          You are all set, <br></br>{' '}
          {onboarding?.nickname ? onboarding.nickname + '.' : '[Nickname]'}
        </Heading>
        <SubHeading>
          I'm Mailander, your private <br />
          decision-making sanctuary.
        </SubHeading>
      </div>
      <WhiteCard>
        <WhatYouNeedToKnow>What you need to know about Mailander:</WhatYouNeedToKnow>

        <CardAndIconContainer>
          <Icon src={'/a-trusted-advisor.png'} alt="trusted advisor" />
          <TextContainer>
            <InfoText isInter fontSize="16px" weight={600}>
              A trusted advisor.
            </InfoText>
            <InfoText isInter weight={400} fontSize={'16px'}>
              Bring any question, from board-level decisions to everyday frictions, and I'll help
              you reach a sharper decision.
            </InfoText>
            <RedirectLink
              href="https://mailander.com/how-it-works"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read More
            </RedirectLink>
          </TextContainer>
        </CardAndIconContainer>

        <CardAndIconContainer>
          <Icon src={'your-data.png'} alt="your data" />
          <TextContainer>
            <InfoText isInter fontSize="16px" weight={600}>
              Your data is in your control.
            </InfoText>
            <InfoText isInter weight={400} fontSize={'16px'}>
              You control what you share, and you can erase every chat or your entire account
              whenever you choose.
            </InfoText>
            <RedirectLink
              href="https://mailander.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read Our Privacy Policy
            </RedirectLink>
          </TextContainer>
        </CardAndIconContainer>

        <CardAndIconContainer>
          <Icon src={'there-is-no-other.png'} alt="terms of service" />
          <TextContainer>
            <InfoText isInter fontSize="16px" weight={600}>
              There is no other experience <Breaker /> like this.
            </InfoText>
            <InfoText isInter weight={400} fontSize={'16px'}>
              This is decision science applied to corporate strategy in a way to achieve exceptional
              performance. We create this experience by leveraging enterprise grade security, strict
              privacy safeguards, and the intellectual property to help you attain your
              breakthroughs.
            </InfoText>
            <RedirectLink
              href="https://mailander.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read Our Terms of Use
            </RedirectLink>
          </TextContainer>
        </CardAndIconContainer>
      </WhiteCard>

      {error && (
        <p style={{ color: '#ef4444', fontSize: 14, margin: 0, textAlign: 'center' }}>{error}</p>
      )}

      <ContinueButton type="button" onClick={handleFinish} disabled={isFinishing}>
        {isFinishing ? 'Finishing…' : 'I Understand'}
      </ContinueButton>
    </ContentSection>
  );
};

(AllSetStep as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
(AllSetStep as any).fill = true;
(AllSetStep as any).smallLogo = true;
(AllSetStep as any).progress = 'Personalize';
(AllSetStep as any).showLogo = false;
